import * as React from "react";

import { EnhancedStore } from "@reduxjs/toolkit";
import {
  fireEvent,
  render,
  RenderOptions,
  RenderResult,
  screen,
  waitFor,
  waitForElementToBeRemoved,
} from "@testing-library/react";

import { ExperimentVariantProvider } from "client/components/Experiment/Experiment";
import { AppProviders } from "client/context";
import { configureAppStore } from "client/store";

import { BASE_ROUTE } from "./route-utils";
interface ExperimentOptions {
  flag: string;
  variant: string;
}

interface Options extends RenderOptions {
  experimentOptions?: ExperimentOptions;
  initialReduxState?: any;
  route?: string | null;
}

const testOptions: Options = {
  experimentOptions: {
    flag: "testFlag",
    variant: "testVariant",
  },
};

// we need access in tests to our store
let store: EnhancedStore;

const customRender = (
  ui: React.ReactElement,
  options: Options = testOptions
): RenderResult => {
  const route = options.route || BASE_ROUTE;
  window.history.pushState({}, "test page", route);
  store = configureAppStore(options.initialReduxState);
  store.dispatch = jest.fn(store.dispatch);
  const { experimentOptions } = options;

  const AllProviders: React.FC = ({ children }) => (
    <React.Suspense fallback={<div />}>
      <AppProviders reduxStore={store}>
        <ExperimentVariantProvider experiment={experimentOptions}>
          {children}
        </ExperimentVariantProvider>
      </AppProviders>
    </React.Suspense>
  );
  return render(ui, {
    wrapper: AllProviders as React.ComponentType,
    ...options,
  });
};

const waitForLoadingToFinish = async (): Promise<void> => {
  // wait for loading spinner to show up
  await waitFor(() => {
    expect([...screen.queryAllByRole(/progressbar/i)]).not.toHaveLength(0);
  });
  // then wait for it to removed
  await waitForElementToBeRemoved(
    () => [...screen.queryAllByRole(/progressbar/i)],
    {
      timeout: 5000,
    }
  );
};

const completeUserInput = (
  inputElement: HTMLElement,
  value: string | number | undefined
): void => {
  fireEvent.change(inputElement, {
    target: { value: value },
  });
  fireEvent.blur(inputElement);
};

const getExpandedAccordionHeader = ({
  expanded,
  name,
}: {
  expanded: boolean;
  name: string | RegExp;
}) =>
  screen.getByRole("button", {
    expanded,
    name,
  });

const isRunningJest = (): boolean => Boolean(process.env.JEST_WORKER_ID);

// override render method
export {
  getExpandedAccordionHeader,
  completeUserInput,
  customRender as render,
  store,
  waitForLoadingToFinish,
  isRunningJest,
};
